import axios from 'axios';

import { env } from 'config';
import { FundRequestTab, OperationType, UserRole } from 'enums';
import {
  FundsRequest,
  CreateFundsRequest,
  QueryParams,
  PaginatedData,
  CreatePlatformFundsRequest,
  ExportPayload,
  Sorting,
} from 'types';
import { apiUtils } from 'utils';

const url = `${env.apiUrl}/funds-requests`;
const exportUrl = `${env.dataApiUrl}/funds-requests/export`;

const createWithdrawal = async (
  fundsRequest: CreateFundsRequest,
): Promise<FundsRequest> =>
  (await axios.post(`${url}/withdrawal`, fundsRequest)).data;

const createPlatformWithdrawal = apiUtils.withAxiosInstance<
  CreatePlatformFundsRequest,
  FundsRequest
>(
  async (axiosInstance, fundsRequest) =>
    (await axiosInstance.post(`${url}/withdrawal/platform`, fundsRequest)).data,
);
const createDepositForUser = apiUtils.withAxiosInstance<
  {
    userId: string;
    fundsRequest: CreateFundsRequest;
  },
  FundsRequest
>(
  async (axiosInstance, { userId, fundsRequest }) =>
    (await axiosInstance.post(`${url}/deposit/${userId}`, fundsRequest)).data,
);

const createWithdrawalForUser = apiUtils.withAxiosInstance<
  {
    userId: string;
    fundsRequest: CreateFundsRequest;
  },
  FundsRequest
>(
  async (axiosInstance, { userId, fundsRequest }) =>
    (await axiosInstance.post(`${url}/withdrawal/${userId}`, fundsRequest))
      .data,
);

const createDeposit = async (
  fundsRequest: CreateFundsRequest,
): Promise<FundsRequest> =>
  (await axios.post(`${url}/deposit`, fundsRequest)).data;

const revoke = async (fundsRequest: FundsRequest): Promise<FundsRequest> =>
  (await axios.patch(`${url}/${fundsRequest.id}/revoke`)).data;

const processCancel = async (
  fundsRequest: FundsRequest,
): Promise<FundsRequest> =>
  (await axios.patch(`${url}/${fundsRequest.id}/process/cancel`)).data;

const processComplete = apiUtils.withAxiosInstance<
  FundsRequest & {
    sendTransaction?: boolean;
  },
  FundsRequest
>(async (axiosInstance, fundsRequest) => {
  const dto =
    fundsRequest.operationType === OperationType.Withdrawal
      ? {
          hash: fundsRequest.hash,
          sendTransaction: fundsRequest.sendTransaction,
        }
      : null;
  return (
    await axiosInstance.patch(`${url}/${fundsRequest.id}/process/complete`, dto)
  ).data;
});

const getPaginatedByUrl =
  (url: string) =>
  async (params: QueryParams): Promise<PaginatedData<FundsRequest>> =>
    (await apiUtils.getWithQueryParams(url, params)).data;

const getAllMyPaginated = getPaginatedByUrl(`${url}/my`);

const getMyActivePaginated = getPaginatedByUrl(`${url}/my/active`);

const getMyArchivePaginated = getPaginatedByUrl(`${url}/my/archive`);

const getAllPaginatedByRole = (role: UserRole, tab: FundRequestTab) =>
  getPaginatedByUrl(`${url}/${role}/${tab}`);

const remove = async (fundsRequest: FundsRequest): Promise<FundsRequest> =>
  (await axios.delete(`${url}/${fundsRequest.id}`)).data;

const exportFundsRequests =
  () =>
  ({
    data,
    filters,
    sorting,
  }: {
    data: ExportPayload;
    filters: string;
    sorting: Sorting;
  }) =>
    apiUtils.requestDownload(
      {
        url: `${exportUrl}`,
        method: 'post',
        data,
        params: { filters, ...sorting },
      },
      `${data.state}-funds-requests-${Date.now()}.xlsx`,
    );

const getExportColumns = () => async (): Promise<{ columns: string[] }> =>
  (await axios.get(`${exportUrl}/columns`)).data;

export const fundsRequestsApi = {
  getAllMyPaginated,
  getMyActivePaginated,
  getMyArchivePaginated,
  createWithdrawal,
  createPlatformWithdrawal,
  createDeposit,
  createDepositForUser,
  createWithdrawalForUser,
  revoke,
  remove,
  processCancel,
  processComplete,
  exportFundsRequests,
  getExportColumns,
  getAllPaginatedByRole,
};
